/*eslint-disable max-lines*/
import {
  useAlarmUpdates,
  useSharedRawAlarmUpdates,
} from '@hakimo-ui/hakimo/data-access';
import { getLocationAlarmUpdateEvents } from '@hakimo-ui/hakimo/feature-shared';
import {
  LocationAlarmUpdatesDTO,
  ScanAlarmUpdateType,
  UnifiedMonitoringProduct,
} from '@hakimo-ui/hakimo/types';
import {
  BasicPanel,
  QueryResult,
  Timeline,
} from '@hakimo-ui/hakimo/ui-elements';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { ReactElement } from 'react';
import { UseQueryResult } from 'react-query';

const REFETCH_INTERVAL = 60000;
interface ScanRawAlarmUpdatesProps {
  eventId: string;
  timestamp: string;
  productType?: UnifiedMonitoringProduct;
  sharedToken?: string;
  alarmGroupId?: string;
}
dayjs.extend(utc);

export function ScanRawAlarmUpdates(props: ScanRawAlarmUpdatesProps) {
  const {
    eventId,
    timestamp,
    productType = UnifiedMonitoringProduct.SCAN,
    sharedToken,
    alarmGroupId,
  } = props;

  const renderContent = (
    queryResult: UseQueryResult<LocationAlarmUpdatesDTO, Error>
  ) => (
    <QueryResult queryResult={queryResult}>
      {(data) => {
        const filteredData: LocationAlarmUpdatesDTO = {
          // ...data,
          items: eventId
            ? data.items.filter((u) => u.event_id === eventId)
            : data.items,
        };

        for (const item of filteredData.items) {
          if (item.update_type === ScanAlarmUpdateType.LLM_EVENT) {
            filteredData.items.push({
              ...item,
              update_type: ScanAlarmUpdateType.STATUS_UPDATE,
              update_text: `changed status to ${
                item.recommendation?.toLowerCase() === 'resolve'
                  ? 'Resolved by Hakimo'
                  : 'Pending'
              }`,
              user: {
                name: 'Hakimo AI Engine',
                id: 'hakimo-ai-engine',
              },
            });
          }
        }

        const events = getLocationAlarmUpdateEvents(
          filteredData,
          false,
          productType,
          false,
          alarmGroupId
        );

        return (
          <div className="max-h-[30rem] overflow-auto px-6 pb-2">
            {events.length > 0 ? (
              <Timeline events={events} />
            ) : (
              <div className="pt-4">No updates</div>
            )}
          </div>
        );
      }}
    </QueryResult>
  );

  return (
    <BasicPanel title="Alarm Updates">
      {sharedToken ? (
        <SharedScanRawAlarmUpdates sharedToken={sharedToken}>
          {renderContent}
        </SharedScanRawAlarmUpdates>
      ) : (
        <RegularScanRawAlarmUpdates eventId={eventId} timestamp={timestamp}>
          {renderContent}
        </RegularScanRawAlarmUpdates>
      )}
    </BasicPanel>
  );
}

export default ScanRawAlarmUpdates;

interface RegularScanRawAlarmUpdatesProps {
  eventId: string;
  timestamp: string;
  children: (
    queryResult: UseQueryResult<LocationAlarmUpdatesDTO, Error>
  ) => ReactElement;
}

function RegularScanRawAlarmUpdates(props: RegularScanRawAlarmUpdatesProps) {
  const { eventId, timestamp, children } = props;

  const searchParams = new URLSearchParams();
  searchParams.append('product', 'scan');

  if (timestamp) {
    const date = dayjs.utc(timestamp);
    const partitionKey = date.format('YYYYMM');
    searchParams.append('partitionKey', partitionKey);
  }

  const queryResult = useAlarmUpdates(
    eventId,
    searchParams.toString(),
    REFETCH_INTERVAL
  );
  const mappedResult = {
    ...queryResult,
    data: queryResult.data && {
      items: queryResult.data.items.map((u) => ({
        update_time: u.update_time,
        update_type: u.update_type,
        user: u.user,
        recommendation: u.recommendation,
        summary: u.summary,
        event_id: u.event_id,
      })),
    },
  } as UseQueryResult<LocationAlarmUpdatesDTO, Error>;

  return children(mappedResult);
}

interface SharedScanRawAlarmUpdatesProps {
  sharedToken: string;
  children: (
    queryResult: UseQueryResult<LocationAlarmUpdatesDTO, Error>
  ) => ReactElement;
}

function SharedScanRawAlarmUpdates(props: SharedScanRawAlarmUpdatesProps) {
  const { sharedToken, children } = props;

  const queryResult = useSharedRawAlarmUpdates(sharedToken);

  return children(queryResult);
}
